import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

export class BomaErrorResponse {
  message: string;
  errorCode: string;

  constructor(init: Partial<BomaErrorResponse>) {
    Object.assign(this, init);
  }
}

@Injectable()
export class ExceptionInterceptor implements HttpInterceptor {
  private messageDisplayed = false;

  constructor(
    private toastSvc: MessageService,
    private router: Router
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      map((event: HttpEvent<any>) => {
        if (
          event.type === 4 &&
          ((event as any).status === 200 || (event as any).status === 201) &&
          (request.method.toLowerCase() === 'put' ||
            request.method.toLowerCase() === 'post')
        ) {
          if (!this.messageDisplayed) {
            this.toastSvc.add({
              severity: 'success',
              summary: 'Success',
              detail: 'The operation was completed successfully.',
            });
            this.messageDisplayed = true;
            setTimeout(() => {
              this.messageDisplayed = false;
            }, 5000);
          }
        }

        return event;
      }),
      catchError((error: HttpErrorResponse) => {
        const createThrowError = (parsedError) =>
          throwError(this.getBomaErrorResponse(parsedError) || parsedError);
        this.handleErrorNotification(error);
        return error.error instanceof Blob
          ? this.parseErrorBlob(error).pipe(
              switchMap((err) => createThrowError(err))
            )
          : createThrowError(error);
      })
    );
  }

  parseErrorBlob(err: HttpErrorResponse): Observable<any> {
    const reader: FileReader = new FileReader();

    const obs = new Observable((observer) => {
      reader.onloadend = (e) => {
        observer.next(JSON.parse(reader.result as string));
      };
    });
    reader.readAsText(err.error);
    return obs;
  }

  private getBomaErrorResponse(error): BomaErrorResponse | null {
    if (
      error &&
      typeof error === 'object' &&
      !Array.isArray(error) &&
      (error as BomaErrorResponse).message
    ) {
      return new BomaErrorResponse(error);
    }

    return null;
  }

  private handleErrorNotification(exception: HttpErrorResponse) {
    if (this.messageDisplayed) {
      return;
    }

    const { status, error } = exception;
    console.log('An Exception Has Occured', exception);
    if (status >= 500) {
      this.toastSvc.add({
        severity: 'error',
        summary: 'Please try again.',
        detail:
          "We couldn't complete the request, if this happens again please contact support.",
      });
    }
    // form validation errors
    else if (
      status === 400 &&
      error?.errors &&
      error?.title === 'One or more validation errors occurred.'
    ) {
      const { errors } = error;
      const detailMessage = Object.keys(errors)
        .map((k) => errors[k].map((e) => `${k.replace('$.', '')} - ${e}`))
        .reduce((prev, curr) => prev.concat(curr), [])
        .join('\n');

      this.toastSvc.add({
        severity: 'warn',
        summary: 'One or more validation errors occurred.',
        detail: detailMessage,
      });
    }
    // form validation errors
    else if (
      status === 400 &&
      error &&
      typeof error === typeof '' &&
      error.length
    ) {
      this.toastSvc.add({
        severity: 'warn',
        summary: 'Something went wrong.',
        detail: error,
      });
    } else if (
      status === 400 &&
      error &&
      typeof error === 'object' &&
      !Array.isArray(error) &&
      (error as BomaErrorResponse).message
    ) {
      console.log('Showing boma error response');
      this.toastSvc.add({
        severity: 'warn',
        summary: 'Something went wrong',
        detail: (error as BomaErrorResponse).message,
      });
    }
    // Unauthorized
    else if (status === 401 && (error?.errors || typeof error === 'string')) {
      let detailMessage;
      if (error?.errors) {
        const { errors } = error;
        detailMessage = Object.keys(errors)
          .map((k) => errors[k].map((e) => `${k.replace('$.', '')} - ${e}`))
          .reduce((prev, curr) => prev.concat(curr), [])
          .join('\n');
      } else {
        detailMessage = error;
      }

      this.toastSvc.add({
        severity: 'warn',
        summary: 'Unauthorized',
        detail: detailMessage,
      });
    } else if (status === 401) {
      this.toastSvc.add({
        severity: 'warn',
        summary: 'Unauthorised.',
        detail:
          'You are unauthorised to view this module, login or contact your system admin.',
      });
    } else if (
      status === 404 &&
      error &&
      typeof error === typeof '' &&
      error.length
    ) {
      this.toastSvc.add({
        severity: 'warn',
        summary: 'Not Found.',
        detail: error,
      });
    } else if (
      (exception as any)?.errorCode === 'interaction_required' &&
      (exception as any)?.errorMessage?.indexOf(
        'User does not have an existing session and request prompt parameter has'
      ) !== -1
    ) {
      //console.log('caught token error in interceptor.');
      // window.localStorage.clear();
      // window.sessionStorage.clear();
      // (window.location as any) = '/';
      this.toastSvc.add({
        severity: 'warn',
        summary: 'You do not have an active session, please log in.',
        detail: error,
      });
    }
    // No permission
    else if (status === 403) {
      this.toastSvc.add({
        severity: 'error',
        summary: `Not Authorised`,
        detail:
          'You do not sufficient permissions to complete this request, speak to your organisation admin.',
      });
    }
    // anything else
    else {
      this.toastSvc.add({
        severity: 'error',
        summary: `Something unexpected happened.`,
        detail: 'Please contact support&#64;boma.today.',
      });
    }

    this.messageDisplayed = true;
    setTimeout(() => {
      console.log('MORE MESSAGE DISPLAYED');
      this.messageDisplayed = false;
    }, 5000);
  }
}
